// Variants in the product form (options and the grid of combinations) and on
// a group's page (its variants with stock).
import type { ProductRow } from '@ims/data'
import { Badge, Button, Card, Checkbox, Field, IconButton, Input, TD, TH, TR, Table } from '@ims/ui'
import { Plus, Trash2 } from '@ims/ui/icons'
import { MoneyInput, QtyInput } from '../components.tsx'
import { money, qty } from '../lib/format.ts'
import { useOpen } from '../lib/motion.ts'
import {
  MAX_OPTIONS,
  comboKey,
  parseOptionValues,
  readOptions,
  readValues,
  variantCombos,
  variantLabel,
  variantSku,
  type VariantOptions,
  type VariantValues,
} from '../lib/variants.ts'

export interface OptionDraft {
  key: string
  name: string
  values: string
}

/** What the user typed for one combination (only the fields they changed). */
export interface VariantEdit {
  include?: boolean
  sku?: string
  barcode?: string
  pricePaisa?: number | null
  openingMilli?: number | null
}

export interface VariantDraft {
  key: string
  values: VariantValues
  existing: ProductRow | null
  include: boolean
  sku: string
  barcode: string
  pricePaisa: number | null
  openingMilli: number | null
}

export function optionDrafts(product: ProductRow | null): OptionDraft[] {
  const options = readOptions(product?.variant_options)
  const drafts = Object.entries(options).map(([name, values]) => ({
    key: crypto.randomUUID(),
    name,
    values: values.join(', '),
  }))
  return drafts.length > 0 ? drafts : [{ key: crypto.randomUUID(), name: 'Size', values: '' }]
}

export function draftOptions(options: readonly OptionDraft[]): VariantOptions {
  const out: VariantOptions = {}
  for (const o of options) {
    const name = o.name.trim()
    const values = parseOptionValues(o.values)
    if (name && values.length > 0) out[name] = values
  }
  return out
}

/**
 * The grid: every existing variant plus every combination of the options,
 * with the user's edits on top of the defaults (SKU from the group's SKU).
 */
export function variantDrafts(
  options: VariantOptions,
  existing: readonly ProductRow[],
  edits: Readonly<Record<string, VariantEdit>>,
  baseSku: string,
): VariantDraft[] {
  const rows: VariantDraft[] = []
  const seen = new Set<string>()
  for (const v of existing) {
    const values = readValues(v.variant_values)
    const key = comboKey(values)
    seen.add(key)
    const e = edits[key] ?? {}
    rows.push({
      key,
      values,
      existing: v,
      include: true,
      sku: e.sku ?? v.sku,
      barcode: e.barcode ?? v.barcode ?? '',
      pricePaisa: e.pricePaisa !== undefined ? e.pricePaisa : v.sell_price_paisa,
      openingMilli: null,
    })
  }
  for (const values of variantCombos(options)) {
    const key = comboKey(values)
    if (seen.has(key)) continue
    seen.add(key)
    const e = edits[key] ?? {}
    rows.push({
      key,
      values,
      existing: null,
      include: e.include ?? true,
      sku: e.sku ?? variantSku(baseSku || 'SKU', values, options),
      barcode: e.barcode ?? '',
      pricePaisa: e.pricePaisa ?? null,
      openingMilli: e.openingMilli ?? null,
    })
  }
  return rows
}

export function VariantsEditor({
  options,
  onOptions,
  drafts,
  onEdit,
  groupPrice,
  trackStock,
  allowsDecimal,
  settings,
}: {
  options: OptionDraft[]
  onOptions: (options: OptionDraft[]) => void
  drafts: VariantDraft[]
  onEdit: (key: string, edit: VariantEdit) => void
  groupPrice: number | null
  trackStock: boolean
  allowsDecimal: boolean
  settings: Parameters<typeof money>[1]
}) {
  const setOption = (key: string, change: Partial<OptionDraft>) =>
    onOptions(options.map((o) => (o.key === key ? { ...o, ...change } : o)))
  const parsed = draftOptions(options)
  return (
    <div className="space-y-3 sm:col-span-2">
      <p className="text-xs text-muted">
        Each combination becomes its own product with its own SKU, barcode, price and stock. Details
        above (category, VAT, brand…) are shared by all of them.
      </p>
      {options.map((o, i) => (
        <div key={o.key} className="grid grid-cols-[1fr_2fr_auto] items-end gap-2">
          <Field label={`Option ${i + 1}`}>
            {(id) => (
              <Input
                id={id}
                value={o.name}
                placeholder="Size"
                onChange={(e) => setOption(o.key, { name: e.target.value })}
              />
            )}
          </Field>
          <Field
            label={`Values of option ${i + 1}`}
            hint={i === 0 ? 'Separate with commas.' : undefined}
          >
            {(id) => (
              <Input
                id={id}
                value={o.values}
                placeholder="S, M, L"
                onChange={(e) => setOption(o.key, { values: e.target.value })}
              />
            )}
          </Field>
          <IconButton
            label={`Remove option ${o.name || i + 1}`}
            className="mb-1"
            disabled={options.length === 1}
            onClick={() => onOptions(options.filter((x) => x.key !== o.key))}
          >
            <Trash2 className="size-4" />
          </IconButton>
        </div>
      ))}
      {options.length < MAX_OPTIONS && (
        <Button
          size="sm"
          variant="secondary"
          icon={<Plus className="size-3.5" />}
          onClick={() =>
            onOptions([
              ...options,
              { key: crypto.randomUUID(), name: options.length === 1 ? 'Color' : '', values: '' },
            ])
          }
        >
          Add option
        </Button>
      )}
      {drafts.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full text-sm">
            <thead className="bg-background text-left text-xs text-muted">
              <tr>
                <th className="px-2 py-1.5">Variant</th>
                <th className="px-2 py-1.5">SKU</th>
                <th className="px-2 py-1.5">Barcode</th>
                <th className="px-2 py-1.5">Price</th>
                {trackStock && <th className="px-2 py-1.5">Opening stock</th>}
              </tr>
            </thead>
            <tbody>
              {drafts.map((d) => {
                const label = variantLabel(d.values, parsed)
                return (
                  <tr key={d.key} className="border-t border-border">
                    <td className="px-2 py-1.5 whitespace-nowrap">
                      {d.existing ? (
                        <span className="font-medium">{label}</span>
                      ) : (
                        <Checkbox
                          label={label}
                          aria-label={`Include ${label}`}
                          checked={d.include}
                          onChange={(e) => onEdit(d.key, { include: e.target.checked })}
                        />
                      )}
                    </td>
                    <td className="px-2 py-1">
                      <Input
                        aria-label={`SKU of ${label}`}
                        value={d.sku}
                        disabled={!d.include}
                        onChange={(e) => onEdit(d.key, { sku: e.target.value })}
                      />
                    </td>
                    <td className="px-2 py-1">
                      <Input
                        aria-label={`Barcode of ${label}`}
                        value={d.barcode}
                        disabled={!d.include}
                        onChange={(e) => onEdit(d.key, { barcode: e.target.value })}
                      />
                    </td>
                    <td className="w-32 px-2 py-1">
                      <MoneyInput
                        aria-label={`Price of ${label}`}
                        paisa={d.pricePaisa}
                        disabled={!d.include}
                        placeholder={groupPrice !== null ? money(groupPrice, settings) : '0.00'}
                        onValue={(p) => onEdit(d.key, { pricePaisa: p })}
                      />
                    </td>
                    {trackStock && (
                      <td className="w-28 px-2 py-1">
                        {!d.existing && (
                          <QtyInput
                            aria-label={`Opening stock of ${label}`}
                            milli={d.openingMilli}
                            allowsDecimal={allowsDecimal}
                            disabled={!d.include}
                            placeholder="0"
                            onValue={(m) => onEdit(d.key, { openingMilli: m })}
                          />
                        )}
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

/** A group's variants with price and stock (on the group's page). */
export function VariantsCard({
  group,
  variants,
  stock,
  unitSymbol,
  settings,
}: {
  group: ProductRow
  variants: ProductRow[]
  stock: ReadonlyMap<string, number>
  unitSymbol: string
  settings: Parameters<typeof money>[1]
}) {
  const openItem = useOpen()
  const options = readOptions(group.variant_options)
  const total = variants.reduce((a, v) => a + (stock.get(v.id) ?? 0), 0)
  return (
    <Card className="mt-4 p-0">
      <h2 className="flex items-center justify-between border-b border-border px-5 py-3 font-semibold">
        Variants
        <span className="text-sm font-normal text-muted">
          {variants.length} · {qty(total)} {unitSymbol} in stock
        </span>
      </h2>
      <Table>
        <thead>
          <tr>
            <TH>Variant</TH>
            <TH>SKU</TH>
            <TH>Barcode</TH>
            <TH align="right">Price</TH>
            <TH align="right">In stock</TH>
          </tr>
        </thead>
        <tbody>
          {variants.map((v) => (
            <TR key={v.id} onClick={() => openItem(`/products/${v.id}`)}>
              <TD className="font-medium">
                {variantLabel(readValues(v.variant_values), options) || v.name}
                {!v.is_active && (
                  <Badge className="ml-2" tone="neutral">
                    Inactive
                  </Badge>
                )}
              </TD>
              <TD className="text-muted">{v.sku}</TD>
              <TD className="text-muted">{v.barcode}</TD>
              <TD align="right">{money(v.sell_price_paisa, settings)}</TD>
              <TD align="right">{v.track_stock ? qty(stock.get(v.id) ?? 0) : '—'}</TD>
            </TR>
          ))}
        </tbody>
      </Table>
    </Card>
  )
}
